import { useState, useCallback, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  ActionIcon,
  Menu,
  Text,
  Paper,
  Stack,
  Skeleton,
  Switch,
  Textarea,
  Pagination,
} from '@mantine/core';
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconFileDescription,
  IconLicense,
  IconTransform,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { customersService } from '../../services/api/customers';
import { useCreateMutation, useUpdateMutation, useFormModal, useTableSort, showValidationError } from '../../hooks';
import { useDisclosure } from '@mantine/hooks';
import { SearchToolbar, FormModal, SortableHeader, DetailModal, DetailField } from '../../components/shared';
import type { Customer, CreateCustomerInput, UpdateCustomerInput } from '../../types';
import { formatPhone } from '../../utils/formatPhone';
import { BulkActionsModal } from './BulkActionsModal';
import dayjs from 'dayjs';

const INITIAL_FORM_DATA: Partial<CreateCustomerInput> = { allowAutoTransfer: true };

export function CustomersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [viewItem, setViewItem] = useState<Customer | null>(null);

  const [bulkModalOpened, { open: openBulkModal, close: closeBulkModal }] = useDisclosure(false);
  const [bulkCustomer, setBulkCustomer] = useState<Customer | null>(null);

  const createModal = useFormModal<CreateCustomerInput>({ initialData: INITIAL_FORM_DATA });
  const editModal = useFormModal<UpdateCustomerInput, Customer>({ initialData: {} });

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['customers', debouncedSearch],
    queryFn: () => customersService.list({ query: debouncedSearch || undefined, limit: 100 }),
  });

  const createMutation = useCreateMutation(
    (data: CreateCustomerInput) => customersService.create(data),
    {
      queryKeys: ['customers', 'customers-list'],
      entityName: 'Customer',
      onSuccessCallback: createModal.close,
    }
  );

  const updateMutation = useUpdateMutation(
    ({ id, data }: { id: string; data: UpdateCustomerInput }) => customersService.update(id, data),
    {
      queryKeys: ['customers'],
      entityName: 'Customer',
      onSuccessCallback: editModal.close,
    }
  );

  const handleCreateSubmit = () => {
    const missingName = !createModal.formData.name?.trim();
    const missingEmail = !createModal.formData.email?.trim();
    if (missingName && missingEmail) {
      showValidationError('Name and Email are required');
      return;
    }
    if (missingName) {
      showValidationError('Name is required');
      return;
    }
    if (missingEmail) {
      showValidationError('Email is required');
      return;
    }
    createMutation.mutate(createModal.formData as CreateCustomerInput);
  };

  const handleEditSubmit = () => {
    if (!editModal.selectedItem) return;
    const missingName = !editModal.formData.name?.trim();
    const missingEmail = !editModal.formData.email?.trim();
    if (missingName && missingEmail) {
      showValidationError('Name and Email are required');
      return;
    }
    if (missingName) {
      showValidationError('Name is required');
      return;
    }
    if (missingEmail) {
      showValidationError('Email is required');
      return;
    }
    updateMutation.mutate({
      id: editModal.selectedItem.id,
      data: editModal.formData as UpdateCustomerInput,
    });
  };

  const openEdit = (customer: Customer) => {
    editModal.openForEdit(customer, {
      name: customer.name,
      email: customer.email || '',
      organization: customer.organization || '',
      phone: customer.phone || '',
      notes: customer.notes || '',
      allowAutoTransfer: customer.allowAutoTransfer,
    });
  };

  const resetPage = useCallback(() => setPage(1), []);
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort<Customer>(resetPage, { column: 'name' });

  const customers = customersData?.customers || [];

  const sortAccessors: Record<string, (item: Customer) => unknown> = useMemo(() => ({
    name: (c) => c.name,
    email: (c) => c.email,
    phone: (c) => c.phone || '',
    licenses: (c) => c.activeLicenseCount || 0,
    reports: (c) => c.reportCount || 0,
    auto_transfer: (c) => c.allowAutoTransfer,
    created: (c) => c.createdAt,
  }), []);

  const sortedCustomers = sortData(customers, sortAccessors);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedCustomers.length / PAGE_SIZE);
  const paginatedCustomers = sortedCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Customers</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={createModal.open}>
          Create Customer
        </Button>
      </Group>

      <SearchToolbar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        placeholder="Search customers..."
      />

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : customers.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No customers found
          </Text>
        ) : (
          <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableHeader column="name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Name</SortableHeader>
                <SortableHeader column="email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Email</SortableHeader>
                <SortableHeader column="phone" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Phone</SortableHeader>
                <SortableHeader column="licenses" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Licenses</SortableHeader>
                <SortableHeader column="reports" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Reports</SortableHeader>
                <SortableHeader column="auto_transfer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Auto Transfer</SortableHeader>
                <SortableHeader column="created" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Created</SortableHeader>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedCustomers.map(customer => (
                <Table.Tr key={customer.id} onClick={() => { setViewItem(customer); openViewModal(); }} style={{ cursor: 'pointer' }}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {customer.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {customer.email || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {formatPhone(customer.phone) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Badge color="green" variant="light">
                        {customer.activeLicenseCount || 0} Active
                      </Badge>
                      {(customer.inactiveLicenseCount || 0) > 0 && (
                        <Badge color="gray" variant="light">
                          {customer.inactiveLicenseCount} Inactive
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {customer.reportCount || 0}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={customer.allowAutoTransfer ? 'green' : 'gray'}
                      variant="light"
                    >
                      {customer.allowAutoTransfer ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {dayjs(customer.createdAt).format('MMM D, YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withArrow>
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconLicense size={14} />}
                          onClick={() => navigate(`/licenses?customerId=${customer.id}`)}
                        >
                          View Licenses
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconTransform size={14} />}
                          onClick={() => { setBulkCustomer(customer); openBulkModal(); }}
                        >
                          Bulk Actions
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconFileDescription size={14} />}
                          onClick={() => navigate(`/customers/${customer.id}/reports`)}
                        >
                          View Reports
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => openEdit(customer)}
                        >
                          Edit
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedCustomers.length)}–{Math.min(page * PAGE_SIZE, sortedCustomers.length)} of {sortedCustomers.length}
            </Text>
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            )}
          </Group>
          </>
        )}
      </Paper>

      {/* View Customer Modal */}
      {viewItem && (
        <DetailModal
          opened={viewModalOpened}
          onClose={closeViewModal}
          title="Customer Details"
          recordId={viewItem.id}
          actions={[
            {
              label: 'Edit',
              icon: <IconEdit size={16} />,
              onClick: () => { closeViewModal(); openEdit(viewItem); },
            },
          ]}
        >
          <DetailField label="Name" value={viewItem.name} />
          <DetailField label="Email" value={viewItem.email} />
          <DetailField label="Organization" value={viewItem.organization} />
          <DetailField label="Phone" value={formatPhone(viewItem.phone)} />
          <DetailField label="Status" value={
            <Badge color={viewItem.status === 'active' ? 'green' : viewItem.status === 'suspended' ? 'orange' : 'red'} variant="light">{viewItem.status}</Badge>
          } />
          <DetailField label="Auto Transfer" value={
            <Badge color={viewItem.allowAutoTransfer ? 'green' : 'gray'} variant="light">{viewItem.allowAutoTransfer ? 'Enabled' : 'Disabled'}</Badge>
          } />
          <DetailField label="Active Licenses" value={String(viewItem.activeLicenseCount || 0)} />
          <DetailField label="Inactive Licenses" value={String(viewItem.inactiveLicenseCount || 0)} />
          <DetailField label="Notes" value={viewItem.notes} multiline />
          <DetailField label="Created" value={dayjs(viewItem.createdAt).format('MMM D, YYYY h:mm A')} />
          <DetailField label="Updated" value={dayjs(viewItem.updatedAt).format('MMM D, YYYY h:mm A')} />
        </DetailModal>
      )}

      {/* Create Customer Modal */}
      <FormModal
        opened={createModal.isOpen}
        onClose={createModal.close}
        title="Create Customer"
        isLoading={createMutation.isPending}
        onSubmit={handleCreateSubmit}
        submitLabel="Create"
      >
        <TextInput
          label="Name"
          placeholder="Customer name"
          required
          value={createModal.formData.name || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, name: e.target.value })}
        />
        <TextInput
          label="Email"
          placeholder="customer@example.com"
          type="email"
          required
          value={createModal.formData.email || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, email: e.target.value })}
        />
        <TextInput
          label="Organization"
          placeholder="Company or organization name"
          value={createModal.formData.organization || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, organization: e.target.value })}
        />
        <TextInput
          label="Phone"
          placeholder="+1 (555) 123-4567"
          value={createModal.formData.phone || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, phone: e.target.value })}
        />
        <Textarea
          label="Notes"
          placeholder="Internal notes about this customer..."
          minRows={3}
          value={createModal.formData.notes || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, notes: e.target.value })}
        />
        <Switch
          label="Allow Auto Transfer"
          description="Allow end-users to self-service transfer activations between devices"
          checked={createModal.formData.allowAutoTransfer ?? true}
          onChange={e => createModal.setFormData({ ...createModal.formData, allowAutoTransfer: e.currentTarget.checked })}
        />
      </FormModal>

      {/* Edit Customer Modal */}
      <FormModal
        opened={editModal.isOpen}
        onClose={editModal.close}
        title="Edit Customer"
        isLoading={updateMutation.isPending}
        onSubmit={handleEditSubmit}
        submitLabel="Save Changes"
      >
        <TextInput
          label="Name"
          placeholder="Customer name"
          required
          value={editModal.formData.name || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, name: e.target.value })}
        />
        <TextInput
          label="Email"
          placeholder="customer@example.com"
          type="email"
          required
          value={editModal.formData.email || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, email: e.target.value })}
        />
        <TextInput
          label="Organization"
          placeholder="Company or organization name"
          value={editModal.formData.organization || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, organization: e.target.value })}
        />
        <TextInput
          label="Phone"
          placeholder="+1 (555) 123-4567"
          value={editModal.formData.phone || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, phone: e.target.value })}
        />
        <Textarea
          label="Notes"
          placeholder="Internal notes about this customer..."
          minRows={3}
          value={editModal.formData.notes || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, notes: e.target.value })}
        />
        <Switch
          label="Allow Auto Transfer"
          description="Allow end-users to self-service transfer activations between devices"
          checked={editModal.formData.allowAutoTransfer ?? true}
          onChange={e => editModal.setFormData({ ...editModal.formData, allowAutoTransfer: e.currentTarget.checked })}
        />
      </FormModal>

      {/* Bulk Actions Modal */}
      <BulkActionsModal
        opened={bulkModalOpened}
        onClose={closeBulkModal}
        customer={bulkCustomer}
      />
    </Container>
  );
}
